import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { PopoverDialog, usePopoverDialog, useFormField, Button } from '@frontend/design-system';
import { useScheduleActionsContainerMethod } from '../../../../../../context/ScheduleActionsContainerContext';
import { useGetAppointmentStatusList } from '../../../../../../hooks';
import { useScheduleActionsAppointmentListInfoShallowStore } from '../../../../../../stores/use-schedule-actions-appointment-list-store';
import { getFilteredAppointments } from '../../../../helpers';
import { FilterButton } from './FilterButton';
import { FilterModal } from './FilterModal';

export const FiltersContainer = () => {
  const { t } = useTranslation('scheduleCalendarActions');
  const { selectedLocationIds } = useAppScopeStore();
  const [appointmentStatuses, setAppointmentStatuses] = useState<string[]>([]);
  const { selectedDate: scheduleActionsDate } = useScheduleActionsContainerMethod();
  const {
    defaultAppointmentList,
    setAppointmentList,
    setStatusFilter,
    statusFilter,
    setProviderList,
    hasActiveFilters,
    setHasActiveFilters,
    providerList,
    defaultProviderList,
    insuranceStatusFilters,
    setInsuranceStatusFilters,
    selectedOperatoryList,
    setSelectedOperatoryList,
    selectedAppointmentTypeList,
    setSelectedAppointmentTypeList,
  } = useScheduleActionsAppointmentListInfoShallowStore(
    'defaultAppointmentList',
    'setAppointmentList',
    'setStatusFilter',
    'statusFilter',
    'setProviderList',
    'setHasActiveFilters',
    'hasActiveFilters',
    'providerList',
    'defaultProviderList',
    'insuranceStatusFilters',
    'setInsuranceStatusFilters',
    'selectedOperatoryList',
    'setSelectedOperatoryList',
    'selectedAppointmentTypeList',
    'setSelectedAppointmentTypeList'
  );

  const {
    getDialogProps,
    getTriggerProps,
    close: closeFilterModal,
  } = usePopoverDialog<HTMLAnchorElement | HTMLButtonElement>({
    placement: 'bottom-end',
  });

  const resetFilters = () => {
    setAppointmentList(defaultAppointmentList);
    setHasActiveFilters(false);
    setStatusFilter('');
    setProviderList([]);
    setInsuranceStatusFilters([]);
    setSelectedOperatoryList([]);
    setSelectedAppointmentTypeList([]);
  };

  useEffect(() => {
    if (
      statusFilter ||
      !!insuranceStatusFilters.length ||
      !!providerList.length ||
      !!selectedAppointmentTypeList.length
    ) {
      const appointmentArray = getFilteredAppointments({
        appointmentsToFilter: defaultAppointmentList,
        providerList: defaultProviderList.filter((provider) => providerList.includes(provider.id ?? '')),
        appointmentStatus: statusFilter && statusFilter !== 'all' ? statusFilter : '',
        selectedAppointmentTypes: selectedAppointmentTypeList,
        selectedOperatoryList: selectedOperatoryList,
      });
      setAppointmentList(appointmentArray);
    } else {
      setAppointmentList(defaultAppointmentList);
    }
  }, [statusFilter, providerList, scheduleActionsDate, selectedAppointmentTypeList]);

  useEffect(() => {
    if (hasActiveFilters) {
      const appointmentArray = getFilteredAppointments({
        appointmentsToFilter: defaultAppointmentList,
        providerList: defaultProviderList.filter((provider) => providerList.includes(provider.id ?? '')),
        appointmentStatus: !!statusFilter && statusFilter !== 'all' ? statusFilter : '',
        selectedAppointmentTypes: selectedAppointmentTypeList,
        selectedOperatoryList: selectedOperatoryList,
      });
      setAppointmentList(appointmentArray);
    }
  }, [hasActiveFilters, providerList, scheduleActionsDate, defaultProviderList, statusFilter, defaultAppointmentList]);

  const providerCheckListField = useFormField(
    {
      type: 'checklist',
      value: providerList,
    },
    [providerList]
  );

  const appointmentStatusRadioField = useFormField(
    {
      type: 'radio',
      value: statusFilter || 'all',
    },
    [statusFilter]
  );

  const insuranceStatusCheckListField = useFormField(
    {
      type: 'checklist',
      value: insuranceStatusFilters,
    },
    [insuranceStatusFilters]
  );

  const operatoryCheckListField = useFormField(
    {
      type: 'checklist',
      value: selectedOperatoryList,
    },
    [selectedOperatoryList]
  );

  const appointmentStatusList = useGetAppointmentStatusList({
    locationIds: selectedLocationIds,
  });

  useEffect(() => {
    setAppointmentStatuses(appointmentStatusList);
  }, [selectedLocationIds]);

  const appointmentTypesListField = useFormField(
    {
      type: 'checklist',
      value: selectedAppointmentTypeList,
    },
    [selectedAppointmentTypeList]
  );

  return (
    <>
      <FilterButton triggerProps={getTriggerProps} />
      <Button
        variant='tertiary'
        onClick={resetFilters}
        disabled={!hasActiveFilters}
        css={{
          color: theme.colors.primary50,
          fontWeight: 'bold',
          height: '100%',
        }}
      >
        {t('Reset')}
      </Button>
      <PopoverDialog
        css={css`
          width: 260px;
          padding: ${theme.spacing(1, 2)};
          border-radius: ${theme.borderRadius.small};
        `}
        {...getDialogProps()}
      >
        <FilterModal
          closeFilterModal={closeFilterModal}
          appointmentStatuses={appointmentStatuses}
          providerCheckListField={providerCheckListField}
          appointmentStatusRadioField={appointmentStatusRadioField}
          insuranceStatusCheckListField={insuranceStatusCheckListField}
          operatoryCheckListField={operatoryCheckListField}
          appointmentTypeCheckListField={appointmentTypesListField}
        />
      </PopoverDialog>
    </>
  );
};
